<template>
	<div
		v-if="IS_DEVELOPMENT"
		class="relative"
	>
			<!-- Wrench Icon Button -->
			<button
				class="absolute z-10 p-2 transition-colors rounded-lg top-2 right-2 bg-mx-gray-200 dark:bg-mx-green-700 hover:bg-mx-gray-300 dark:hover:bg-mx-green-800"
				@click="isOpen = !isOpen"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					stroke-width="1.5"
					stroke="currentColor"
					class="w-4 h-4 text-mx-gray-600 dark:text-mx-gray-300"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437 1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008Z"
					/>
				</svg>
			</button>

			<!-- Variant Testing Panel -->
			<Transition name="slide-down">
				<div
					v-if="isOpen"
					class="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-mx-green-700 rounded-b-xl shadow-lg border-b-2 border-mx-gray-300 dark:border-mx-green-500 p-4 z-20 max-h-[400px] overflow-y-auto"
				>
					<div class="flex flex-col gap-3">
						<h3 class="mb-2 text-sm font-bold text-black dark:text-mx-gray-200">
							Variant Testing
						</h3>

						<!-- Variant Groups -->
						<div
							v-for="group in variantGroups"
							:key="group.label || 'default'"
							class="flex flex-col gap-2"
						>
							<!-- Group Label (optional) -->
							<label
								v-if="group.label"
								class="text-xs font-semibold text-gray-600 dark:text-mx-gray-300"
							>
								{{ group.label }}
							</label>

							<!-- Variant Buttons -->
							<div class="flex flex-wrap gap-2">
								<button
									v-for="variant in group.variants"
									:key="variant.id"
									class="px-3 py-1 text-xs rounded"
									:class="getVariantClass(variant)"
									@click="$emit('set-variant', variant.id)"
								>
									{{ variant.label }}
								</button>
							</div>
						</div>

						<!-- Reset Button -->
						<button
							class="px-4 py-2 mt-2 text-xs font-semibold text-black rounded bg-mx-gray-200 dark:bg-mx-green-600 dark:text-mx-gray-200 hover:bg-mx-gray-300 dark:hover:bg-mx-green-500"
							@click="$emit('reset')"
						>
							{{ resetLabel }}
						</button>
					</div>
				</div>
			</Transition>
		</div>
</template>

<script setup lang="ts">
import { IS_DEVELOPMENT } from '@/constants'

interface Variant {
	id: string
	label: string
	colorClass?: 'green' | 'yellow' | 'orange' | 'red' | 'gray' | 'blue'
}

interface VariantGroup {
	label?: string
	variants: Variant[]
}

interface Props {
	variantGroups: VariantGroup[]
	resetLabel?: string
}

withDefaults(defineProps<Props>(), {
	resetLabel: 'Reset'
})

defineEmits<{
	'set-variant': [variantId: string]
	'reset': []
}>()

const isOpen = ref(false)

const getVariantClass = (variant: Variant) => {
	const colorMap = {
		green: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800',
		yellow: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-200 dark:hover:bg-yellow-800',
		orange: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 hover:bg-orange-200 dark:hover:bg-orange-800',
		red: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-800',
		gray: 'bg-mx-gray-100 dark:bg-mx-green-900 text-black dark:text-mx-gray-200 hover:bg-mx-gray-200 dark:hover:bg-mx-green-600',
		blue: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800'
	}

	return colorMap[variant.colorClass || 'gray']
}
</script>

<style scoped>
/* Slide-down transition */
.slide-down-enter-active,
.slide-down-leave-active {
	transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
	transform: translateY(-100%);
	opacity: 0;
}

.slide-down-enter-to,
.slide-down-leave-from {
	transform: translateY(0);
	opacity: 1;
}
</style>
